// DashboardTabs.jsx
import React from 'react';
import {
  Box, Tabs, Tab, Typography, Divider, Button, useMediaQuery,
  BottomNavigation, BottomNavigationAction, IconButton, Tooltip,
} from '@mui/material';
import { CalendarMonth, Home, Schedule, Lock, Settings, Devices, Logout, Business } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import DashboardTabsStyles from './DashboardTabs.styles';

// 🔽 NEW: role/title helper + auth
import { useAuth } from '../../hooks/useAuth';
import { useUserRole } from '../../helpers/userRole';

const tabItems = [
  { label: 'Manage Properties', icon: <Home /> },
  { label: 'Scheduled Viewings', icon: <Schedule /> },
  { label: 'Smart Locks', icon: <Lock /> },
  { label: 'Account Settings', icon: <Settings /> },
  { label: 'Device Management', icon: <Devices /> },
  { label: 'Vendor Requests', icon: <Business /> },
];

// 🔽 add optional dashboardTitle + userType + onLogout props
const DashboardTabs = ({
  tabValue,
  onTabChange,
  onCalendarAccess,
  onLogout,           // new prop for logout action
  children,
  dashboardTitle,     // optional explicit title
  userType,           // optional raw userType (e.g., "agent", "landlord", "manager", "owner")
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Get current user and derive title from centralized helper
  const { currentUser } = useAuth();
  const { dashboardTitle: helperTitle, isRoleReady } = useUserRole(currentUser);

  const computedTitle = React.useMemo(() => {
    // 1) explicit prop wins
    if (dashboardTitle) return dashboardTitle;

    // 2) prefer helper (Firestore/Auth-normalized) when ready
    if (isRoleReady) return helperTitle;

    // 3) fallback to legacy prop-based logic if provided
    const r = (userType || '').toLowerCase();
    if (r === 'agent') return 'Agent Dashboard';
    if (['manager', 'property_manager', 'landlord', 'owner'].includes(r)) {
      return 'Property Owner Dashboard';
    }
    return 'Dashboard';
  }, [dashboardTitle, helperTitle, isRoleReady, userType]);

  const handleMobileChange = (event, newValue) => onTabChange(event, newValue);

  // 🎨 black styles (respecting light/dark)
  const black = theme.palette.common.black;
  const blackHover = theme.palette.mode === 'dark'
    ? '#000' // keep true black in dark mode
    : '#111'; // slight lift in light mode

  return (
      <Box sx={DashboardTabsStyles.container}>
      <Box sx={DashboardTabsStyles.header}>
        {/* 🔁 dynamic title */}
        <Typography variant="h4">{computedTitle}</Typography>

        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: { xs: 1, sm: 1.25 },
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          width: { xs: '100%', sm: 'auto' },
        }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<CalendarMonth />}
            onClick={onCalendarAccess}
            sx={{ 
              borderRadius: 999,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              px: { xs: 1.5, sm: 2 },
              py: { xs: 0.75, sm: 1 },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            Access Calendar
          </Button>

          {/* 🖤 Black Logout (icon-only on mobile, pill on desktop) */}
          {onLogout && (
            isMobile ? (
              <Tooltip title="Logout">
                <IconButton
                  onClick={onLogout}
                  aria-label="logout"
                  sx={{
                    bgcolor: black,
                    color: 'white',
                    borderRadius: 2,
                    '&:hover': { bgcolor: blackHover },
                    width: 40,
                    height: 40,
                  }}
                >
                  <Logout fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                onClick={onLogout}
                startIcon={<Logout />}
                sx={{
                  bgcolor: black,
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 999,
                  px: 1.75,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  '&:hover': { bgcolor: blackHover },
                  '&:active': { transform: 'translateY(1px)' },
                }}
              >
                Logout
              </Button>
            )
          )}
        </Box>
      </Box>

      {/* Tabs for desktop, BottomNavigation for mobile */}
      {!isMobile ? (
        <Tabs
          value={tabValue}
          onChange={onTabChange}
          sx={DashboardTabsStyles.tabs}
          indicatorColor="primary"
          textColor="primary"
        >
          {tabItems.map((item) => (
            <Tab
              key={item.label}
              label={item.label}
              icon={item.icon}
              iconPosition="start"
            />
          ))}
        </Tabs>
      ) : (
        <BottomNavigation
          value={tabValue}
          onChange={handleMobileChange}
          showLabels
          sx={{
            ...DashboardTabsStyles.bottomNav,
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1300,
            boxShadow: theme.shadows[6],
            background: theme.palette.background.paper,
          }}
        >
          {tabItems.map((item, idx) => (
            <BottomNavigationAction
              key={item.label}
              label={item.label}
              icon={item.icon}
              value={idx}
            />
          ))}
        </BottomNavigation>
      )}

      <Divider sx={{ my: { xs: '1rem', sm: '1.5rem', md: '2rem' } }} />
      <Box sx={{ 
        pb: { xs: 8, sm: 0 },
        width: '100%',
        overflowX: 'hidden',
      }}>
        {children}
      </Box>
    </Box>
  );
};

export default DashboardTabs;
